// Google Analytics 4 con "modo de consentimiento" (mismo criterio que learn2.work).
//
// Cómo funciona:
// - Al cargar cualquier página, GA4 arranca con las cookies DENEGADAS.
// - Si la persona acepta en el aviso de cookies, se habilitan las cookies de análisis.
// - La elección se guarda en el navegador para no volver a preguntar.
// - El sitio no usa publicidad: esas cookies quedan siempre denegadas.
//
// Eventos que usamos (mismos nombres en learn2.work y aleromanisio.com):
// - generate_lead  → { form_name }  cuando se envía un formulario
// - file_download  → { file_name }  cuando se descarga un recurso (automático, ver abajo)

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: Gtag;
  }
}

const ID_MEDICION = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
const CLAVE_CONSENTIMIENTO = 'consentimiento_cookies';
export const EVENTO_ABRIR_PREFERENCIAS = 'abrir-preferencias-cookies';

export type Consentimiento = 'aceptado' | 'rechazado';

export function leerConsentimiento(): Consentimiento | null {
  try {
    const valor = localStorage.getItem(CLAVE_CONSENTIMIENTO);
    return valor === 'aceptado' || valor === 'rechazado' ? valor : null;
  } catch {
    return null; // navegador sin acceso a localStorage (modo privado, bloqueos)
  }
}

export function guardarConsentimiento(valor: Consentimiento): void {
  try {
    localStorage.setItem(CLAVE_CONSENTIMIENTO, valor);
  } catch {
    // si no se puede guardar, igual aplicamos la elección para esta visita
  }
  aplicarConsentimiento(valor);
}

function aplicarConsentimiento(valor: Consentimiento): void {
  if (typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    analytics_storage: valor === 'aceptado' ? 'granted' : 'denied',
  });
}

// ---------- Visitas propias (de Ale) ----------
// Para que las pruebas de Ale no se mezclen con las visitas reales, cada navegador suyo se
// marca una vez entrando al sitio con ?interno=si (y se desmarca con ?interno=no).
// Los navegadores marcados mandan todos sus eventos con visitante=interno, y el panel los
// excluye. La marca se guarda en este navegador y en este dominio.
const CLAVE_INTERNO = 'visitante_interno';

function esVisitanteInterno(): boolean {
  try {
    const pedido = new URLSearchParams(window.location.search).get('interno');
    if (pedido === 'si') {
      localStorage.setItem(CLAVE_INTERNO, 'si');
      mostrarAvisoBreve('Listo: este navegador quedó marcado como tuyo. Sus visitas no se cuentan en el panel.');
    } else if (pedido === 'no') {
      localStorage.removeItem(CLAVE_INTERNO);
      mostrarAvisoBreve('Listo: este navegador vuelve a contar como visitante común.');
    }
    return localStorage.getItem(CLAVE_INTERNO) === 'si';
  } catch {
    return false;
  }
}

function mostrarAvisoBreve(texto: string): void {
  const aviso = document.createElement('div');
  aviso.setAttribute('role', 'status');
  aviso.textContent = texto;
  aviso.style.cssText =
    'position:fixed;top:12px;left:50%;transform:translateX(-50%);z-index:9999;max-width:90vw;' +
    'background:#23242c;color:#fff;font:13px/1.4 system-ui,sans-serif;padding:8px 14px;border-radius:8px;' +
    'box-shadow:0 4px 12px rgba(0,0,0,.2)';
  const agregar = () => {
    document.body.appendChild(aviso);
    setTimeout(() => aviso.remove(), 6000);
  };
  if (document.body) agregar();
  else document.addEventListener('DOMContentLoaded', agregar);
}

// Se llama una sola vez por página, al arrancar (src/arranque.tsx).
export function iniciarAnalytics(): void {
  if (!ID_MEDICION) return; // sin ID configurado, GA4 queda apagado

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });

  const consentimiento = leerConsentimiento();
  if (consentimiento) aplicarConsentimiento(consentimiento);

  window.gtag('js', new Date());
  // visitante=interno marca las visitas de Ale (ver arriba); traffic_type es el nombre estándar de GA4.
  const interno = esVisitanteInterno();
  window.gtag('config', ID_MEDICION, {
    anonymize_ip: true,
    ...(interno ? { visitante: 'interno', traffic_type: 'internal' } : {}),
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${ID_MEDICION}`;
  document.head.appendChild(script);
}

export function registrarEvento(nombre: string, parametros?: Record<string, unknown>): void {
  if (typeof window.gtag !== 'function') return;
  const interno = (() => {
    try {
      return localStorage.getItem(CLAVE_INTERNO) === 'si';
    } catch {
      return false;
    }
  })();
  window.gtag('event', nombre, interno ? { ...parametros, visitante: 'interno' } : parametros);
}

// Para el link "Preferencias de cookies".
export function abrirPreferenciasCookies(): void {
  window.dispatchEvent(new Event(EVENTO_ABRIR_PREFERENCIAS));
}

// Registra "file_download" cada vez que alguien hace clic en un link de descarga,
// en cualquier página, sin tener que tocar cada componente.
// Cuenta como descarga: links de Google Drive con "export=download", links con el atributo
// "download", o links a archivos (.pdf, .xlsx, .pptx, etc.).
const EXTENSIONES_ARCHIVO = /\.(pdf|xlsx?|csv|docx?|pptx?|zip|png|jpe?g)(\?|#|$)/i;

export function activarSeguimientoDescargas(): void {
  document.addEventListener('click', (evento) => {
    const link = (evento.target as HTMLElement | null)?.closest('a');
    if (!link || !link.href) return;

    const esDescarga =
      link.href.includes('export=download') ||
      link.hasAttribute('download') ||
      EXTENSIONES_ARCHIVO.test(link.href);
    if (!esDescarga) return;

    const nombre = (link.getAttribute('download') || link.innerText || link.href)
      .trim()
      .split('\n')[0]
      .slice(0, 100);

    registrarEvento('file_download', { file_name: nombre, link_url: link.href });
  });
}
